import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { User, LogOut, LayoutDashboard, UserCircle, Settings } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";

interface UserMenuProps {
  onMobileMenuClose?: () => void;
  isMobile?: boolean;
}

const UserMenu: React.FC<UserMenuProps> = ({ onMobileMenuClose, isMobile = false }) => {
  const [user, setUser] = useState<{ email: string } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Set up auth state listener BEFORE getting session
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ? { email: session.user.email || "" } : null);
      setIsLoading(false);
    });

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ? { email: session.user.email || "" } : null);
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      toast.success("Logged out successfully");
      navigate("/");
      onMobileMenuClose?.();
    } catch (error) {
      toast.error("Failed to log out");
    }
  };

  const getInitials = (email: string) => {
    const parts = email.split("@")[0].split(/[._-]/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return email.slice(0, 2).toUpperCase();
  };

  if (isLoading) {
    return null;
  }

  // Mobile layout
  if (isMobile) {
    if (!user) {
      return (
        <Link
          to="/login"
          className="flex items-center gap-2 py-3 text-muted-foreground hover:text-primary transition-colors font-medium"
          onClick={onMobileMenuClose}
        >
          <User size={18} />
          Log In
        </Link>
      );
    }

    return (
      <div className="border-t border-border pt-3 mt-3 space-y-1">
        <div className="py-2 text-sm text-muted-foreground truncate">{user.email}</div>
        <Link
          to="/dashboard"
          className="flex items-center gap-2 py-2 hover:text-primary transition-colors"
          onClick={onMobileMenuClose}
        >
          <LayoutDashboard size={18} />
          My Dashboard
        </Link>
        <Link
          to="/dashboard/profile"
          className="flex items-center gap-2 py-2 hover:text-primary transition-colors"
          onClick={onMobileMenuClose}
        >
          <UserCircle size={18} />
          My Profile
        </Link>
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 py-2 text-destructive hover:text-destructive/80 transition-colors w-full text-left"
        >
          <LogOut size={18} />
          Log Out
        </button>
      </div>
    );
  }

  // Desktop layout - logged out
  if (!user) {
    return (
      <Button variant="ghost" size="sm" asChild>
        <Link to="/login" className="flex items-center gap-2">
          <User size={16} />
          Log In
        </Link>
      </Button>
    );
  }

  // Desktop layout - logged in
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="rounded-full h-9 w-9">
          <Avatar className="h-8 w-8">
            <AvatarFallback className="bg-primary text-primary-foreground text-xs font-medium">
              {getInitials(user.email)}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="font-normal">
          <p className="text-sm font-medium truncate">{user.email}</p>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/dashboard" className="flex items-center gap-2 cursor-pointer">
            <LayoutDashboard size={16} />
            My Dashboard
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/dashboard/profile" className="flex items-center gap-2 cursor-pointer">
            <UserCircle size={16} />
            My Profile
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/dashboard/notifications" className="flex items-center gap-2 cursor-pointer">
            <Settings size={16} />
            Settings
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={handleLogout}
          className="flex items-center gap-2 cursor-pointer text-destructive focus:text-destructive"
        >
          <LogOut size={16} />
          Log Out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default UserMenu;
